import { useState, useCallback, useMemo, useEffect, useRef } from "react";

export interface MultiSelectSlot {
  courtId: string;
  courtName: string;
  startTime: string; // ISO datetime
  endTime: string;
  price: number;
}

interface UseMultiSelectReturn {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  selectedSlots: MultiSelectSlot[];
  toggleSlot: (slot: MultiSelectSlot) => boolean;
  selectSlot: (slot: MultiSelectSlot) => void;
  deselectSlot: (courtId: string, startTime: string) => void;
  isSlotSelected: (courtId: string, startTimeISO: string) => boolean;
  clearSelection: () => void;
  removeSlot: (index: number) => void;
  totalPrice: number;
  isDragging: boolean;
  dragMode: "select" | "deselect";
  startDrag: (courtId: string, startTimeISO: string) => void;
  continueDrag: () => void;
  endDrag: () => void;
  isAtLimit: boolean;
  maxSlots: number;
}

const DEFAULT_MAX_SLOTS = 10;

export function useMultiSelect(selectedDate: Date, maxSlots: number = DEFAULT_MAX_SLOTS): UseMultiSelectReturn {
  const [enabled, setEnabledRaw] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState<MultiSelectSlot[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState<"select" | "deselect">("select");
  const prevDateRef = useRef(selectedDate.getTime());

  // When toggling off, clear selections
  const setEnabled = useCallback((v: boolean) => {
    setEnabledRaw(v);
    if (!v) {
      setSelectedSlots([]);
    }
  }, []);

  // Reset on date change
  useEffect(() => {
    if (selectedDate.getTime() !== prevDateRef.current) {
      prevDateRef.current = selectedDate.getTime();
      setSelectedSlots([]);
    }
  }, [selectedDate]);

  const isSlotSelected = useCallback(
    (courtId: string, startTimeISO: string) =>
      selectedSlots.some((s) => s.courtId === courtId && s.startTime === startTimeISO),
    [selectedSlots]
  );

  const isAtLimit = selectedSlots.length >= maxSlots;

  const toggleSlot = useCallback((slot: MultiSelectSlot): boolean => {
    let added = false;
    setSelectedSlots((prev) => {
      const existingIndex = prev.findIndex(
        (s) => s.courtId === slot.courtId && s.startTime === slot.startTime
      );
      if (existingIndex >= 0) {
        return prev.filter((_, i) => i !== existingIndex);
      }
      if (prev.length >= maxSlots) {
        return prev;
      }
      added = true;
      return [...prev, slot];
    });
    return added;
  }, [maxSlots]);

  const selectSlot = useCallback((slot: MultiSelectSlot) => {
    setSelectedSlots((prev) => {
      const exists = prev.some(
        (s) => s.courtId === slot.courtId && s.startTime === slot.startTime
      );
      if (exists || prev.length >= maxSlots) return prev;
      return [...prev, slot];
    });
  }, [maxSlots]);

  const deselectSlot = useCallback((courtId: string, startTime: string) => {
    setSelectedSlots((prev) =>
      prev.filter((s) => !(s.courtId === courtId && s.startTime === startTime))
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedSlots([]);
  }, []);

  const removeSlot = useCallback((index: number) => {
    setSelectedSlots((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const totalPrice = useMemo(
    () => selectedSlots.reduce((sum, s) => sum + s.price, 0),
    [selectedSlots]
  );

  const startDrag = useCallback(
    (courtId: string, startTimeISO: string) => {
      const alreadySelected = selectedSlots.some(
        (s) => s.courtId === courtId && s.startTime === startTimeISO
      );
      setIsDragging(true);
      setDragMode(alreadySelected ? "deselect" : "select");
    },
    [selectedSlots]
  );

  // continueDrag is a no-op on the hook level — the actual select/deselect
  // is performed by the caller using selectSlot/deselectSlot based on dragMode
  const continueDrag = useCallback(() => {}, []);

  const endDrag = useCallback(() => {
    setIsDragging(false);
  }, []);

  return {
    enabled,
    setEnabled,
    selectedSlots,
    toggleSlot,
    selectSlot,
    deselectSlot,
    isSlotSelected,
    clearSelection,
    removeSlot,
    totalPrice,
    isDragging,
    dragMode,
    startDrag,
    continueDrag,
    endDrag,
    isAtLimit,
    maxSlots,
  };
}
